import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useCmsContent } from '@/hooks/useCmsContent';
import HeroIllustration from '@/components/HeroIllustration';
import PageSEO from '@/components/PageSEO';
import { 
  ArrowRight, 
  Rocket, 
  Globe, 
  Calculator, 
  FileText, 
  Users, 
  CheckCircle,
  Star,
  TrendingUp,
  ShieldCheck,
  ExternalLink,
  Newspaper,
  Cpu
} from 'lucide-react';

const HomePage = () => {
  const { t: cms } = useCmsContent('homepage');
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const tx = (cmsKey, i18nKey) => lang === 'it' ? (cms(cmsKey) || t(i18nKey)) : t(i18nKey);
  const services = [
    {
      icon: Rocket,
      title: 'Startup & Nuovi Business',
      description: 'Consulenza completa per l\'avvio e la gestione di attività innovative.',
      span: 'lg:col-span-2'
    },
    {
      icon: Globe,
      title: 'E-commerce & Web',
      description: 'Supporto fiscale per vendite online e attività digitali.',
      span: ''
    },
    {
      icon: TrendingUp,
      title: 'Fiscalità Internazionale',
      description: 'Gestione redditi esteri e convenzioni contro le doppie imposizioni.',
      span: ''
    },
    {
      icon: Calculator,
      title: 'Contabilità & Bilanci',
      description: 'Tenuta contabile, bilanci annuali e adempimenti fiscali.',
      span: ''
    },
    {
      icon: FileText,
      title: 'Fatturazione Elettronica',
      description: 'Gestione completa del ciclo attivo e passivo SDI.',
      span: ''
    }
  ];

  const features = t('home.features', { returnObjects: true });

  return (
    <div className="min-h-screen" data-testid="home-page">
      <PageSEO path="/" />
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-white grain-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium mb-6">
                <Star className="w-4 h-4" />
                {tx('hero_badge', 'home.badge')}
              </span>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 leading-none mb-6">
                {t('home.title1')}<br />
                <span className="hero-text">{t('home.titleHighlight')}</span><br />
                {t('home.title2')}
              </h1>
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
                {tx('hero_description', 'home.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/consulenza">
                  <Button size="lg" className="btn-accent w-full sm:w-auto" data-testid="cta-consultation">
                    {t('home.ctaPrimary')}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/servizi">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="cta-services">
                    {t('home.ctaSecondary')}
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <HeroIllustration />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="bg-slate-900 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-6 lg:gap-12">
            {features.map((feature, index) => (
              <motion.div
                key={feature}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * index }}
                className="flex items-center gap-2 text-white"
              >
                <CheckCircle className="w-5 h-5 text-indigo-400" />
                <span className="text-sm font-medium">{feature}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Press Mention */}
      <section className="py-8 bg-white border-b border-slate-100" data-testid="press-mention-banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.a
            href="https://legalcommunity.it/lexant-e-azzano-con-mbm-holding-nella-cessione-di-morandi-bortot-al-gruppo-saviola/"
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 group cursor-pointer"
          >
            <div className="flex items-center gap-2 text-slate-400 text-xs font-medium tracking-widest uppercase">
              <Newspaper className="w-4 h-4" />
              {t('home.pressLabel')}
            </div>
            <div className="h-4 w-px bg-slate-200 hidden sm:block" />
            <p className="text-sm sm:text-base text-slate-700 text-center group-hover:text-indigo-600 transition-colors">
              <span className="font-semibold">Legalcommunity.it</span> — {t('home.pressText')}
            </p>
            <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 transition-colors flex-shrink-0" />
          </motion.a>
        </div>
      </section>

      {/* Services Bento Grid */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-sm font-medium tracking-widest uppercase text-indigo-600 mb-4 block">
              {t('home.servicesTitle')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {tx('services_title', 'home.servicesSubtitle')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('servizi.subtitle')}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Featured: Innovazione Digitale */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="md:col-span-2 lg:col-span-3 bg-gradient-to-br from-teal-600 to-cyan-700 rounded-2xl p-8 text-white relative overflow-hidden"
              data-testid="featured-service-home"
            >
              <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center gap-6">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0 backdrop-blur-sm">
                  <Cpu className="w-7 h-7 text-white" />
                </div>
                <div className="flex-1">
                  <span className="text-xs font-medium uppercase tracking-widest text-teal-200 mb-1 block">
                    {lang === 'en' ? 'Featured Service' : 'Servizio in Evidenza'}
                  </span>
                  <h3 className="text-2xl font-bold mb-2">
                    {lang === 'en' ? 'Digital Innovation' : 'Innovazione Digitale'}
                  </h3>
                  <p className="text-teal-100">
                    {lang === 'en' 
                      ? 'Support for startups, SMEs and professionals in developing custom software and digital applications with AI technologies.'
                      : 'Affiancamento a startup, PMI e professionisti nello sviluppo di software e applicativi digitali personalizzati con tecnologie AI.'}
                  </p>
                </div>
                <Link to="/servizi" className="flex-shrink-0">
                  <Button size="lg" className="bg-white text-teal-700 hover:bg-teal-50">
                    {lang === 'en' ? 'Learn More' : 'Scopri di più'}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>

            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 * index }}
                className={`service-card p-8 ${service.span}`}
                data-testid={`service-card-${index}`}
              >
                <div className="w-14 h-14 bg-indigo-50 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-indigo-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-slate-600">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/servizi">
              <Button size="lg" variant="outline" data-testid="view-all-services">
                {t('home.ctaSecondary')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-slate-900 rounded-3xl p-8 lg:p-16 text-center"
          >
            <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-8">
              <Users className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {tx('cta_title', 'home.ctaTitle')}
            </h2>
            <p className="text-lg text-slate-300 mb-8 max-w-2xl mx-auto">
              {tx('cta_description', 'home.ctaSubtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/consulenza">
                <Button size="lg" className="btn-accent w-full sm:w-auto" data-testid="cta-book-consultation">
                  {t('home.ctaPrimary')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/area-clienti">
                <Button size="lg" variant="outline" className="w-full sm:w-auto bg-transparent text-white border-white/30 hover:bg-white/10" data-testid="cta-client-area">
                  {t('nav.areaClienti')}
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Online Consultation Banner */}
      <section className="py-12 bg-indigo-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h3 className="text-2xl font-bold text-white mb-2">
                {tx('banner_title', 'consulenza.badge')}
              </h3>
              <p className="text-indigo-100">
                {t('home.subtitle')}
              </p>
            </div>
            <Link to="/consulenza">
              <Button size="lg" className="bg-white text-indigo-600 hover:bg-slate-100" data-testid="online-consultation-cta">
                {t('home.ctaSecondary')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
